"use client";

import { useEffect, useEffectEvent } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { Effect, type Scope } from "effect";
import { RpcClient } from "@effect/rpc";
import type { PlatformScope } from "@/domain/scope/platform-scope";
import { useCurrentPlatformScope, withRequestScope } from "@/shared/scope";
import { chatKeys } from "./keys";
import { useCallStore } from "./call-store";
import { callEngine } from "./call-engine";
import { chatEq, ChatRpc } from "./api/chat-client";

/**
 * Live chat push over AWS Chime SDK Messaging — the ring channel AND the message/
 * unread refresh signal. Replaces the retired held-open SSE + Upstash poll (the
 * 2026-07-16 near-prod-outage): the browser now holds ONE managed WebSocket to
 * Chime (never to a Vercel function), and the server only PUBLISHES events.
 *
 * The wire payload is IDS ONLY (`{ c, m, k, u }`) and NON_PERSISTENT — Chime stores
 * nothing; the client uses each event purely to drive the incoming-call modal or
 * invalidate the affected TanStack queries. Postgres stays the source of truth.
 *
 * Auth: the backend mints SCOPED, short-lived creds (`getChatRealtimeSession` →
 * STS federation, limited to chime:Connect on this user) that sign the WebSocket.
 * We reconnect shortly before they expire. Closes on a hidden tab (a backgrounded
 * tab need not ring, matching prior behaviour) and reopens on return; queries
 * refetch on focus so nothing is missed. Fail-open: if the session can't start,
 * chat still works via the queries' own polling/refetch.
 *
 * Mount ONCE app-wide (see `ChatRealtimeBridge`) — one connection per tab.
 *
 * SCOPE (FIX-SCOPE-STORE-3, D27). The listener sits in the ROOT layout (and the
 * underwriter shell), above every section, so its scope is the route's
 * (`useCurrentPlatformScope`), read at the moment it is needed: the grant mint
 * runs in it, and a ring stamps it on the call store so the call it starts
 * keeps it after the user navigates away.
 */
const run = <A, E>(program: Effect.Effect<A, E, RpcClient.Protocol | Scope.Scope>, scope: PlatformScope): Promise<A> => Effect.runPromise(program.pipe(Effect.scoped, Effect.provide(chatEq.layer), withRequestScope(scope)));

/**
 * How long a hidden tab keeps its connection before we tear it down. Reopening
 * is expensive (grant RPC + new AWS SDK client + new WebSocket), so we absorb
 * ordinary window-switching rather than rebuilding on every visibility flip.
 */
const HIDDEN_CLOSE_DELAY_MS = 5 * 60 * 1000;

/** Lower bound on the credential-refresh reconnect. See its use site. */
const RECONNECT_FLOOR_MS = 5 * 60 * 1000;

/** The single Chime MessagingSession observer this hook registers. */
interface MessagingObserver {
  messagingSessionDidReceiveMessage: (message: {
    type: string;
    payload: string;
  }) => void;
}
interface WireEvent {
  readonly c?: string;
  readonly m?: string;
  readonly k?: string;
  readonly u?: string;
}

/** The cached inbox row shape this module reads — a structural subset of
 *  ConversationSummaryView, kept local so a view change can't silently widen it. */
interface CachedInboxPage {
  readonly items?: ReadonlyArray<{
    readonly conversationId: string;
    readonly unread: boolean;
  }>;
}

/**
 * Does the CACHED inbox already show this conversation as unread?
 *
 * Answers conservatively: anything we can't prove (inbox not cached, thread not
 * on a loaded page, a brand-new conversation) returns false, which means "go
 * refetch". So this can only ever skip work that provably changes nothing — it
 * can never invent a wrong badge count.
 *
 * Safe against staleness because `markRead` invalidates the inbox and the unread
 * key together, so a read thread can't linger in cache as unread. A cross-tab
 * read is the one gap, and that tab's badge was already stale before this change;
 * refetch-on-focus resolves it.
 *
 * `getQueriesData` (plural) matches by key PREFIX, which matters because both
 * queries append a `scope` this module doesn't know.
 */
export function isKnownUnread(qc: QueryClient, conversationId: string): boolean {
  const entries = qc.getQueriesData<{
    readonly pages?: ReadonlyArray<CachedInboxPage>;
  }>({
    queryKey: chatKeys.conversations
  });
  for (const [, data] of entries) {
    for (const page of data?.pages ?? []) {
      const row = page.items?.find(i => i.conversationId === conversationId);
      if (row) return row.unread;
    }
  }
  return false;
}

/**
 * Route one decoded event to the call store or the affected queries. `scope`
 * is the listener's at the moment the frame lands: a ring stamps it on the
 * call, which is where that call's requests read it from (call-store `scope`).
 */
export function dispatch(qc: QueryClient, parsed: WireEvent, scope: PlatformScope): void {
  const conversationId = parsed.c;

  // ── Call lifecycle (the ring channel) ──────────────────────────────────────
  if (parsed.k === "call_ring") {
    const state = useCallStore.getState();
    if (state.phase !== "idle") return; // busy → never yank into a new ring
    if (!parsed.m || !conversationId || !parsed.u) return;
    state.patch({
      phase: "incoming",
      incoming: {
        callId: parsed.m,
        conversationId,
        fromUserId: parsed.u,
        mode: "ring"
      },
      scope
    });
    return;
  }
  // Mid-call invite (call-parity §10): a targeted "X is adding you to a call".
  // Accepting joins the LIVE room via joinCall — no hunt-group claim.
  if (parsed.k === "call_invite") {
    const state = useCallStore.getState();
    if (state.phase !== "idle") return; // busy → ignore; the invite row keeps
    if (!parsed.m || !conversationId || !parsed.u) return;
    state.patch({
      phase: "incoming",
      incoming: {
        callId: parsed.m,
        conversationId,
        fromUserId: parsed.u,
        mode: "invite"
      },
      scope
    });
    return;
  }
  if (parsed.k === "call_taken") {
    const state = useCallStore.getState();
    if (state.phase === "incoming" && state.incoming?.callId === parsed.m) state.reset();
    // Non-acting devices' Calls tab/inbox refresh in realtime, not on focus.
    if (conversationId) qc.invalidateQueries({
      queryKey: ["chat", "calls", conversationId]
    });
    qc.invalidateQueries({
      queryKey: chatKeys.conversations
    });
    return;
  }
  if (parsed.k === "call_ended") {
    const state = useCallStore.getState();
    if (state.incoming?.callId === parsed.m || state.callId === parsed.m) {
      void callEngine.leave();
      callEngine.endLocally();
    }
    if (conversationId) qc.invalidateQueries({
      queryKey: ["chat", "calls", conversationId]
    });
    qc.invalidateQueries({
      queryKey: chatKeys.conversations
    });
    return;
  }

  // ── Message landed → refresh unread badge, inbox, and any open thread ────────
  // The unread badge is the one query mounted on EVERY page (navbar + sidebar),
  // so it is the only one of these that actually refetches when you're not in
  // the inbox — `invalidateQueries` refetches active queries and merely marks
  // inactive ones stale. That made it the highest-frequency query in the app:
  // one refetch per recipient per tab per message.
  //
  // It only ever reports `unreadConversations` (a count of THREADS, not
  // messages), so a message arriving in a thread the viewer already has unread
  // cannot change the answer. In that case, skip it. The second and every
  // subsequent message in an active conversation now costs nothing.
  if (!conversationId || !isKnownUnread(qc, conversationId)) {
    qc.invalidateQueries({
      queryKey: chatKeys.unread
    });
  }
  qc.invalidateQueries({
    queryKey: chatKeys.conversations
  });
  if (conversationId) {
    qc.invalidateQueries({
      queryKey: chatKeys.messages(conversationId)
    });
    // Also refresh the space's meetings list off the same push. The meetings
    // query (use-live-meeting) is deliberately poll-free — it lives on mount +
    // window focus + this realtime signal — so an event landing in a space is
    // what surfaces a meeting that another participant just started or ended.
    qc.invalidateQueries({
      queryKey: chatKeys.meetings(conversationId)
    });
  }
}
export function useChatRealtime(enabled: boolean): void {
  const qc = useQueryClient();
  const routeScope = useCurrentPlatformScope();
  // Read, not reacted to: a navigation between sections must not tear down
  // and rebuild the one Chime socket this tab holds.
  const scopeNow = useEffectEvent((): PlatformScope => routeScope);
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;

    // `session` is a Chime DefaultMessagingSession and `client` a ChimeSDKMessagingClient;
    // typed loosely because both SDKs are dynamically imported (kept out of the
    // initial bundle). Both are held in effect scope — NOT inside open() — so that
    // close() can actually release them.
    let session: {
      start: () => Promise<void>;
      stop: () => void;
      removeObserver: (observer: MessagingObserver) => void;
    } | null = null;
    let client: {
      destroy: () => void;
    } | null = null;
    let observer: MessagingObserver | null = null;
    let closed = false;
    // Single-flight guard: `session` is only assigned AFTER two awaits (grant +
    // dynamic import), so without this two concurrent open() calls — e.g. the
    // ~12h creds refresh's close()+open() overlapping a tab-visible open() — would
    // both pass the null-checks, each build a ChimeSDKMessagingClient +
    // DefaultMessagingSession (each owning a WebSocket + HTTP handler), and the
    // second assignment would orphan the first beyond close()'s reach: a per-tab
    // leak that compounds across every user. `opening` makes open() re-entrant-safe.
    let opening = false;
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    let hiddenTimer: ReturnType<typeof setTimeout> | null = null;
    const close = () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = null;
      // Detach the observer and destroy the AWS SDK client, not just stop the
      // socket: the observer closes over `qc`, and the SDK client owns an HTTP
      // handler + socket pool. Dropping the references without these two calls
      // strands both for the life of the tab.
      if (session && observer) session.removeObserver(observer);
      observer = null;
      session?.stop();
      session = null;
      client?.destroy();
      client = null;
    };
    const open = async () => {
      if (session || opening || closed) return;
      opening = true;
      try {
        // 1. Mint a scoped session grant (provisions user + channel on demand).
        let grant: Awaited<ReturnType<typeof mintGrant>>;
        try {
          grant = await mintGrant(scopeNow());
        } catch {
          return; // fail-open: queries still poll/refetch without live push
        }
        if (closed || session) return;

        // 2. Open the Chime MessagingSession WebSocket with the scoped creds.
        const [{
          DefaultMessagingSession,
          MessagingSessionConfiguration,
          ConsoleLogger,
          LogLevel
        }, {
          ChimeSDKMessagingClient
        }] = await Promise.all([import("amazon-chime-sdk-js"), import("@aws-sdk/client-chime-sdk-messaging")]);
        if (closed || session) return;
        const c = new ChimeSDKMessagingClient({
          region: grant.region,
          credentials: {
            accessKeyId: grant.accessKeyId,
            secretAccessKey: grant.secretAccessKey,
            sessionToken: grant.sessionToken
          }
        });
        const config = new MessagingSessionConfiguration(grant.userArn, null, undefined, c);
        const s = new DefaultMessagingSession(config, new ConsoleLogger("chat-realtime", LogLevel.ERROR));
        const o: MessagingObserver = {
          messagingSessionDidReceiveMessage(message: {
            type: string;
            payload: string;
          }) {
            if (message.type !== "CREATE_CHANNEL_MESSAGE") return;
            try {
              const outer = JSON.parse(message.payload) as {
                Content?: string;
              };
              if (!outer.Content) return;
              dispatch(qc, JSON.parse(outer.Content) as WireEvent, scopeNow());
            } catch {
              // malformed frame — ignore
            }
          }
        };
        s.addObserver(o);
        client = c;
        observer = o;
        session = s;
        await s.start();

        // The effect may have been torn down (unmount / flag-off) DURING start().
        // We assigned session before start(), so cleanup's close() already ran
        // against a not-yet-started socket; release this now-started one too.
        if (closed) {
          close();
          return;
        }

        // 3. Reconnect ~1 min before the federation creds expire.
        // The floor is a SAFETY VALVE, not a target: grants are ~12h, but if one
        // ever came back short-lived, a low floor would turn every tab into a
        // reconnect loop — each iteration costing a grant RPC and a new socket.
        // Five minutes bounds that blast radius; nothing legitimate hits it.
        const wait = Math.max(RECONNECT_FLOOR_MS, grant.expiresAtMs - Date.now() - 60_000);
        refreshTimer = setTimeout(() => {
          close();
          void open();
        }, wait);
      } finally {
        opening = false;
      }
    };
    const onVisibilityChange = () => {
      if (document.hidden) {
        // Do NOT tear down on the hide itself. Every reopen builds a fresh AWS
        // SDK client + WebSocket + observer, so closing eagerly turns ordinary
        // window-switching into a client-construction loop — which is exactly
        // how multi-window testing ran the browser out of memory. One idle
        // socket for a few minutes is far cheaper than rebuilding the stack on
        // every alt-tab; a genuinely backgrounded tab still releases everything.
        if (!hiddenTimer) {
          hiddenTimer = setTimeout(() => {
            hiddenTimer = null;
            close();
          }, HIDDEN_CLOSE_DELAY_MS);
        }
        return;
      }
      if (hiddenTimer) {
        clearTimeout(hiddenTimer);
        hiddenTimer = null;
      }
      // No-op when the session survived the grace period — the common case.
      void open();
      // A fresh MessagingSession only receives events from connect onward:
      // non-persistent frames that arrived while the tab was hidden are never
      // replayed, and the chat queries' staleTime (unread 60s, conversations 30s)
      // can suppress the focus refetch on a quick tab return. Invalidate the whole
      // chat prefix so the badge, inbox, and any open thread refetch immediately.
      qc.invalidateQueries({
        queryKey: chatKeys.all
      });
    };
    if (!document.hidden) void open();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      closed = true;
      if (hiddenTimer) clearTimeout(hiddenTimer);
      hiddenTimer = null;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      close();
    };
  }, [enabled, qc]);
}

/** Fetch a scoped Chime Messaging session grant from the backend. */
function mintGrant(scope: PlatformScope) {
  return run(Effect.gen(function* () {
    const rpc = yield* RpcClient.make(ChatRpc);
    return yield* rpc.getChatRealtimeSession({});
  }), scope);
}